import React from 'react';
import styled, { css } from 'styled-components';
import { buttonTextStyling } from 'components/generic/typography';
import withAsyncStatus from 'components/hoc/WithAsyncStatus/WithAsyncStatus';
import withSpinner from 'components/hoc/WithSpinner/WithSpinner';
import { withIcon } from 'components/hoc/withIcon/WithIcon';
import CustomAction from 'components/shared/Routing/components/CustomAction/CustomAction';
import type { CustomActionProps } from 'components/shared/Routing/components/CustomAction/shared/types';
import { type ButtonSizeKeys, buttonSizeStyling } from './shared/buttonSizes';
import buttonVariants, { type ButtonVariant } from './shared/buttonVariants';
import type { ButtonProps, ButtonSizesObject } from './shared/types/button';

const buttonDefaultStyling = css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    text-decoration: none;
`;

export const StyledButton = styled(CustomAction)<{
    variant?: ButtonVariant;
    size: ButtonSizesObject | ButtonSizeKeys;
    $disabled?: boolean;
}>`
    //Default styling
    ${buttonDefaultStyling}
    //Font styling
    ${buttonTextStyling}
    //Size styling
    ${buttonSizeStyling}
     //Variant styling
     ${({ variant }) => variant && buttonVariants[variant]}

    ${({ theme }) => (theme as any).ButtonStyling}
`;

export type CustomActionButtonProps = CustomActionProps & ButtonProps<HTMLButtonElement>;

const CustomActionButton = React.forwardRef<HTMLButtonElement, CustomActionButtonProps>(
    (
        {
            action,
            variant = 'primary',
            size = 'md',
            disabled,
            onClick,
            onClickDisabled,
            tabIndex,
            children,
            ...restProps
        },
        ref
    ) => (
        <StyledButton
            action={action}
            ref={ref}
            variant={variant}
            size={size}
            $disabled={disabled}
            tabIndex={disabled ? -1 : tabIndex}
            onClick={(event) => {
                if (disabled) {
                    if (onClickDisabled) onClickDisabled(event);
                    return; // prevents usual onClick events
                }
                if (onClick) onClick(event);
            }}
            {...restProps}
        >
            {children}
        </StyledButton>
    )
);

export default Object.assign(CustomActionButton, {
    WithIcon: withIcon(CustomActionButton),
    WithAsyncStatus: withAsyncStatus(CustomActionButton),
    WithSpinner: withSpinner(withAsyncStatus(CustomActionButton))
});
